import {
  defineOverridesPreferences,
  definePreferencesExtension,
} from '@vben/preferences';

interface WebAntdPreferencesExtension {
  defaultTableSize: number;
  enableFormFullscreen: boolean;
  reportTitle: string;
  tenantMode: 'multi' | 'single';
}

/**
 * @description 项目配置文件
 * 只需要覆盖项目中的一部分配置，不需要的配置不用覆盖，会自动使用默认配置
 * !!! 更改配置后请清空缓存，否则可能不生效
 */
export const overridesPreferences = defineOverridesPreferences({
  // overrides
  app: {
    /** 后端路由模式 */
    accessMode: 'backend',
    /**
     * 顶栏/登录页 Logo 旁显示的项目名（简称"智汇云"）。
     * 浏览器标签页全名仍由 .env 的 VITE_APP_TITLE（智汇云平台）控制，二者解耦。
     */
    name: '智汇云',
    enableRefreshToken: true,
    /**
     * 关闭 vben 内置"检查更新"弹框（默认开启、每 1 分钟轮询）。
     * 发布生产构建后 index.html 的 etag 变化会触发"点击刷新以获取最新版本"，发布期频繁弹。
     * 如想保留更新提示、只降频，改为 checkUpdatesInterval: 30（单位分钟）。
     */
    enableCheckUpdates: false,
  },
  logo: {
    /**
     * 后台登录页与顶栏 Logo（矢量源文件 public/logo.svg，覆盖 Vben 默认 Logo）。
     * 位图备用：public/logo.png（512x512）、public/logo-full.png（图标+文字横版）。
     */
    source: '/logo.svg',
    sourceDark: '/logo.svg',
    /**
     * 图片缩放方式：contain 保持比例完整显示，避免大图被拉伸变形。
     * 旧值（Vben 默认 cover）会把非正方形大图裁切/压扁。
     */
    fit: 'contain',
    /**
     * 开启侧栏/顶栏「图标 + 文字」并列：文字内容来自 preferences.app.name（此处为"智汇云"）。
     */
    showText: true,
  },
  footer: {
    /** 默认关闭 footer 页脚，因为有一定遮挡 */
    enable: false,
    fixed: false,
  },
  copyright: {
    companyName: import.meta.env.VITE_APP_TITLE,
    companySiteLink: 'https://gitee.com/yudaocode/yudao-ui-admin-vben',
  },
  "theme": {
    "mode": "light"
  }
});

export const preferencesExtension =
  definePreferencesExtension<WebAntdPreferencesExtension>({
    tabLabel: 'preferences.antd.tabLabel',
    title: 'preferences.antd.title',
    fields: [
      {
        component: 'switch',
        defaultValue: true,
        key: 'enableFormFullscreen',
        label: 'preferences.antd.fields.enableFormFullscreen.label',
        tip: 'preferences.antd.fields.enableFormFullscreen.tip',
      },
      {
        component: 'select',
        defaultValue: 'single',
        key: 'tenantMode',
        label: 'preferences.antd.fields.tenantMode.label',
        options: [
          {
            label: 'preferences.antd.fields.tenantMode.options.single.label',
            value: 'single',
          },
          {
            label: 'preferences.antd.fields.tenantMode.options.multi.label',
            value: 'multi',
          },
        ],
      },
      {
        component: 'number',
        componentProps: {
          max: 200,
          min: 10,
          step: 10,
        },
        defaultValue: 20,
        key: 'defaultTableSize',
        label: 'preferences.antd.fields.defaultTableSize.label',
      },
      {
        component: 'input',
        defaultValue: '',
        key: 'reportTitle',
        label: 'preferences.antd.fields.reportTitle.label',
        placeholder: 'preferences.antd.fields.reportTitle.placeholder',
      },
    ],
  });
